import React from 'react';
import { withPrefix } from 'gatsby';
import SlicePadding from 'components/Energy/Layout/SlicePadding';
import { getImageMarkup } from 'utils/getSliceMedia';
import * as styles from './ImageCard.module.css';

const ImageCard = data => {
  const {
    topspacing,
    bottomspacing,
    variant,
    layout,
    type,
    title,
    imageSize,
    customHeight,
    showBlueBar,
    content: { text },
    link: {
      embed: {
        text: linkText,
        attributes: { href, target, rel, title: linkTitle },
      },
    },
    media,
  } = data;

  // Normalize imageSize from Drupal's display text to lowercase values
  // If not set or "full-width", return null to maintain original full-width behavior (no constraints)
  const normalizeImageSize = (size: string): string | null => {
    if (!size) return null;
    const lowerSize = size.toLowerCase();
    // Treat "full-width" and plain "medium" as default (no constraints)
    if (lowerSize === 'medium' || lowerSize.includes('full')) return null;
    if (lowerSize.includes('small')) return 'small';
    if (lowerSize.includes('medium')) return 'medium'; // "Medium (max-width: 600px)"
    if (lowerSize.includes('large')) return 'large';
    return size.toLowerCase();
  };

  // Normalize boolean from Drupal (could be true/false, "1"/"0", 1/0, or undefined)
  // Returns: true, false, or null (for undefined/default behavior)
  const normalizeBoolean = (value: any): boolean | null => {
    if (value === undefined || value === null) return null; // Default behavior
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') return value === '1' || value.toLowerCase() === 'true';
    if (typeof value === 'number') return value === 1;
    return false;
  };

  const normalizedImageSize = normalizeImageSize(imageSize);
  const normalizedShowBlueBar = normalizeBoolean(showBlueBar);
  const textColor = variant ? 'none' : 'reverse ';

  // This is an exception, the video image is slotted inside another slot
  // so can't be accessed through the slotted selector.
  let markup;

  if (media === undefined) {
    markup = '<h1>No media found</h1>';
  } else {
    markup = getImageMarkup(media, 'preview', 'image');
  }

  // Conditional blue bar visibility logic:
  // - true: Show bar only if text exists (can't show empty bar)
  // - false: Never show bar
  // - null/undefined: Show bar only if text exists (default behavior)
  const hasTextContent = text && text.trim().length > 0;
  const shouldShowBlueBar =
    normalizedShowBlueBar === false ? false : hasTextContent;

  return (
    <SlicePadding top={topspacing} bottom={bottomspacing}>
      <div
        className={styles.imageCardWrapper}
        {...(normalizedImageSize && { 'data-size': normalizedImageSize })}
      >
        <atmos-image-card
          {...(shouldShowBlueBar && { variant: textColor })}
          {...(shouldShowBlueBar && { layout })}
          {...(shouldShowBlueBar && { type })}
          title={title}
          {...(shouldShowBlueBar && { text })}
          link={linkText}
          target={target}
          rel={rel}
          link-title={linkTitle}
          url={href && href.length ? withPrefix(href) : null}
          {...(normalizedImageSize && { 'image-size': normalizedImageSize })}
          custom-height={customHeight || ''}
          show-blue-bar={shouldShowBlueBar ? 'true' : 'false'}
          data-hide-bar={!shouldShowBlueBar ? 'true' : 'false'}
          style={!shouldShowBlueBar ? { '--content-display': 'none', '--bar-display': 'none' } : {}}
        >
          {markup}
        </atmos-image-card>
      </div>
    </SlicePadding>
  );
};

export default ImageCard;
